import { useId } from 'react';

import { Route42InlineMarkdown } from '../../../components/ui/Route42Markdown';
import type { InvestigationSynthesisDto } from '../types/investigation';

const changeIcons: Record<string, string> = {
  WHAT_WE_KNOW_CHANGED: 'bi-check2-circle',
  POSSIBLE_CAUSE_STRENGTHENED: 'bi-arrow-up-right',
  POSSIBLE_CAUSE_WEAKENED: 'bi-arrow-down-right',
  OPEN_CHECK_ANSWERED: 'bi-check2-square',
  NEW_CHECK_IDENTIFIED: 'bi-search',
  WHERE_THINGS_STAND_UPDATED: 'bi-arrow-repeat',
  CLOSER_TO_ACTION_OR_CLOSURE: 'bi-signpost-split',
  UNCERTAINTY_CHANGED: 'bi-shield-check',
};

export function InvestigationSynthesisCard({ synthesis }: { synthesis: InvestigationSynthesisDto }) {
  const titleId = useId();
  const unavailable = synthesis.status === 'UNAVAILABLE';
  const material = synthesis.status === 'MATERIAL_CHANGE';
  const headline = unavailable
    ? 'Route42 could not review the latest update'
    : material
      ? 'Route42 reviewed the latest team update'
      : 'Route42 reviewed the latest team update';

  return (
    <section
      className={`r42-investigation-section r42-investigation-synthesis r42-investigation-synthesis--${synthesis.status.toLocaleLowerCase().replaceAll('_', '-')}`}
      aria-labelledby={titleId}
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="r42-investigation-synthesis-head">
        <div className="r42-investigation-synthesis-icon" aria-hidden="true">
          <i className={`bi ${unavailable ? 'bi-cloud-slash' : material ? 'bi-stars' : 'bi-check2-circle'}`} />
        </div>
        <div>
          <span className="r42-eyebrow">What changed</span>
          <h2 id={titleId}>{headline}</h2>
        </div>
      </div>

      <p className="r42-investigation-synthesis-summary"><Route42InlineMarkdown content={synthesis.summary} /></p>

      {synthesis.changes.length ? (
        <ul className="r42-investigation-synthesis-changes" aria-label="Changes to the Investigation">
          {synthesis.changes.map((change, index) => (
            <li key={`${change.change_type}-${index}`}>
              <i className={`bi ${changeIcons[change.change_type] ?? 'bi-dot'}`} aria-hidden="true" />
              <Route42InlineMarkdown content={change.message} />
            </li>
          ))}
        </ul>
      ) : null}

      {synthesis.uncertainty ? (
        <div className="r42-investigation-synthesis-note">
          <strong>Still uncertain</strong>
          <Route42InlineMarkdown content={synthesis.uncertainty} />
        </div>
      ) : null}

      {synthesis.suggested_next_check ? (
        <div className="r42-investigation-synthesis-note">
          <strong>Useful next check</strong>
          <Route42InlineMarkdown content={synthesis.suggested_next_check} />
        </div>
      ) : null}

      {!unavailable ? (
        <p className="r42-investigation-synthesis-role"><i className="bi bi-stars" aria-hidden="true" /> Route42 connects this update to the Investigation and shows how the team's shared understanding changed.</p>
      ) : null}
      {!unavailable && synthesis.assessment_updated ? (
        <p className="r42-investigation-synthesis-footnote"><i className="bi bi-arrow-up" aria-hidden="true" /> Where things stand was updated.</p>
      ) : null}
      {synthesis.reviewed_at ? <time className="r42-investigation-synthesis-time" dateTime={synthesis.reviewed_at}>Reviewed {formatTime(synthesis.reviewed_at)}</time> : null}
    </section>
  );
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}
